import { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import "./SimulationModal.css";
import CurveChart from "../curveChart/CurveChart";
import { PARAMS } from "../../../constants/modelSimulationParams";
import { useTranslation } from "react-i18next";

function normalizeArray(arr, size) {
  const result = [];
  for (let i = 0; i < size; i++) {
    if (i < arr.length && arr[i] !== undefined && arr[i] !== null) {
      result.push(arr[i]);
    } else {
      result.push(result[result.length - 1] ?? arr[0] ?? 0);
    }
  }
  return result;
}

const SimulationModal = ({
  show,
  onClose,
  predictionHours,
  windSpeed,
  windDirection,
  fuelMoisture,
  temperatureParam,
  onInputChange,
  isLightMode,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const n = predictionHours;
  const themeClass = isLightMode ? "light-theme" : "";

  const [localWindSpeed, setLocalWindSpeed] = useState([]);
  const [localWindDirection, setLocalWindDirection] = useState([]);
  const [localFuelMoisture, setLocalFuelMoisture] = useState([]);
  const [localTemperature, setLocalTemperature] = useState([]);

  // Keep refs in sync so handleChange always reads latest without stale closure
  const localWindSpeedRef = useRef(localWindSpeed);
  const localWindDirectionRef = useRef(localWindDirection);
  const localFuelMoistureRef = useRef(localFuelMoisture);
  const localTemperatureRef = useRef(localTemperature);

  useEffect(() => {
    localWindSpeedRef.current = localWindSpeed;
  }, [localWindSpeed]);
  useEffect(() => {
    localWindDirectionRef.current = localWindDirection;
  }, [localWindDirection]);
  useEffect(() => {
    localFuelMoistureRef.current = localFuelMoisture;
  }, [localFuelMoisture]);
  useEffect(() => {
    localTemperatureRef.current = localTemperature;
  }, [localTemperature]);

  // Every time the modal opens OR n changes → re-pad all arrays to exactly n
  useEffect(() => {
    if (!show) return;
    setLocalWindSpeed(normalizeArray(windSpeed, n));
    setLocalWindDirection(normalizeArray(windDirection, n));
    setLocalFuelMoisture(normalizeArray(fuelMoisture, n));
    setLocalTemperature(normalizeArray(temperatureParam, n));
  }, [show, n]); // intentionally only [show, n] — re-run when modal opens or hours change

  // Map key → ref + setter (stable, no stale closure)
  const refMap = useRef({
    windSpeed: { ref: localWindSpeedRef, setter: setLocalWindSpeed },
    windDirection: {
      ref: localWindDirectionRef,
      setter: setLocalWindDirection,
    },
    fuelMoisture: { ref: localFuelMoistureRef, setter: setLocalFuelMoisture },
    temperature: { ref: localTemperatureRef, setter: setLocalTemperature },
  });

  const handleChange = useCallback(
    (paramKey, idx, newVal) => {
      const { ref, setter } = refMap.current[paramKey];
      // Read from ref → always latest state, never stale
      const newArr = normalizeArray(ref.current, n);
      newArr[idx] = newVal;
      setter(newArr);
      onInputChange({ target: { value: String(newVal) } }, paramKey, idx);
    },
    [n, onInputChange],
  );

  const handleSave = useCallback(() => {
    // Read from refs → guaranteed latest values
    const finalWindSpeed = normalizeArray(localWindSpeedRef.current, n);
    const finalWindDirection = normalizeArray(localWindDirectionRef.current, n);
    const finalFuelMoisture = normalizeArray(localFuelMoistureRef.current, n);
    const finalTemperature = normalizeArray(localTemperatureRef.current, n);

    finalWindSpeed.forEach((val, idx) =>
      onInputChange({ target: { value: String(val) } }, "windSpeed", idx),
    );
    finalWindDirection.forEach((val, idx) =>
      onInputChange({ target: { value: String(val) } }, "windDirection", idx),
    );
    finalFuelMoisture.forEach((val, idx) =>
      onInputChange({ target: { value: String(val) } }, "fuelMoisture", idx),
    );
    finalTemperature.forEach((val, idx) =>
      onInputChange({ target: { value: String(val) } }, "temperature", idx),
    );

    onClose();
  }, [n, onInputChange, onClose]);

  const param = PARAMS[activeTab];

  const stateMap = {
    windSpeed: localWindSpeed,
    windDirection: localWindDirection,
    fuelMoisture: localFuelMoisture,
    temperature: localTemperature,
  };

  const displayValues = normalizeArray(stateMap[param.key] ?? [], n);

  return (
    <Modal
      show={show}
      onHide={onClose}
      dialogClassName="sim-modal-dialog"
      centered
      size="xl"
    >
      <div className={`sim-modal-root ${themeClass}`}>
        <div className="sim-modal-header">
          <h5 className="sim-modal-title">{t("simulationModal.title")}</h5>
          <h5 style={{ fontSize: "15px", color: "#f0a500" }}>
            {t("simulationModal.simulationWarning")}
          </h5>
        </div>

        <div className="sim-modal-tabs">
          {PARAMS.map((p, i) => (
            <button
              key={p.key}
              className={`sim-tab${activeTab === i ? " active" : ""}`}
              style={{ "--tab-color": p.color }}
              onClick={() => setActiveTab(i)}
              data-testid={`simulation-modal-tab-${p.key}`}
            >
             {t(p.labelKey)}
            </button>
          ))}
        </div>

        <div className="sim-modal-body">
          <div className="sim-chart-meta">
            <span className="sim-chart-label">
              {t(param.labelKey)} ({t(param.unitKey)})
            </span>
            <span className="sim-chart-hint">
              {t("simulationModal.dragHint")}
            </span>
          </div>
          <CurveChart
            key={param.key}
            param={param}
            values={displayValues}
            n={n}
            onChange={(idx, val) => handleChange(param.key, idx, val)}
            isLightMode={isLightMode}
          />
        </div>

        <div className="sim-modal-footer">
          <Button
            className="event-btn sidebar-primary-btn"
            onClick={handleSave}
            data-testid={`simulation-modal-save-button`}
          >
           {t("simulationModal.save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SimulationModal;
